import styles from "./daily.module.css";

import moment, { Moment, MomentInput } from "moment";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { CanvasChart } from "./canvas-chart/canvas-chart";
import * as TimeConversion from "./utils/time-conversions";
import { useEffect, useState } from "react";
import {
    LeftArrowButton,
    RightArrowButton,
} from "./dashboard-components/arrow-button";
import { TodayButton } from "./dashboard-components/today-button";
import { StatusType } from "./dashboard-components/categories";
import PickerWithButtonField from "./calendar-picker/CalendarButton";
import MultiPickerWithButtonField from "./multi-calendar-picker/MultiCalendarButton";
import { ClientDashboardStore } from "../../store/store";
import { ReactComponent as ChairIcon } from "../../../../components/svg/status-icons/client-in-chair.svg";
import { ReactComponent as VisitorIcon } from "../../../clients-overview/svg/client-visitor-detected.svg";
import CategoryMenu from "./category-menu/CategoryMenu";
import ZoomLevelToggle from "./zoom-level-toggle/ZoomLevelToggle";
import TypeSelector from "./type-selector/TypeSelector";
import { ViewItem, ViewSwitch } from "../../../../components/view-switch";
import CompareDropdown from "./compare-dropdown/CompareDropdown";
import web from "../../web";
import { useParams } from "react-router-dom";
import {
    STATUS_ID,
    STATUS_ID_TO_ICON,
} from "../../../../components/status-icon/status-icon";
import UserSettingsStore, {
    UserSettings,
} from "../../../../store/user-settings";

export interface IDailyView {
    clientId: number;
    clientDashboardStore?: ClientDashboardStore;
    userSettingsStore?: UserSettingsStore;
}

export interface MainCategory {
    icon?: any;
    typeId: number;
    label: string;
    color: string;
    items: any[];
    subcategories: SubCategory[];
    expanded: boolean;
    expandedLocked: boolean;
}

export interface SubCategory {
    icon?: any;
    typeId: number;
    label: string;
    color: string;
    items: any[];
}

export interface Item {
    color: string;
    label: string;
    startTimestamp: number;
    endTimestamp: number | null;
}

export enum ItemType {
    NONE,
    INTERNAL,
    ALERT,
    EXTERNAL,
}

function DailyView(props: IDailyView) {
    const { t } = useTranslation("dashboard");
    const { t: statusesTranslation } = useTranslation("substatus-grid");

    const [timestamp, setTimestamp] = useState<Moment | null>(null);
    const [range1, setRange1] = useState<(Moment | null)[]>([null, null]);
    const [range2, setRange2] = useState<(Moment | null)[]>([null, null]);
    const [compareWeekNumber, setCompareWeekNumber] = useState<number>(1);
    const clientDashboardStore = props.clientDashboardStore!;
    const userSettingsStore = props.userSettingsStore!;

    const statusColorList = [
        "#EB6950",
        "#1AA89C",
        "#F0BEAF",
        "#2D324B",
        "#87A5B4",
    ];

    const [isOutOfBedAvailable, setIsOutOfBedAvailable] =
        useState<boolean>(false);

    const icons = {
        [StatusType.WithSecondPersonInRoom.valueOf()]: <VisitorIcon />,
        [StatusType.InChair.valueOf()]: <ChairIcon fill="#52D9E3" />,
    };

    const ignoredStatuses = [
        StatusType.Inactivity,
        StatusType.Off,
        StatusType.Unknown,
    ];

    const [categoryList1, setCategoryList1] = useState<MainCategory[]>([]);
    const [categoryList2, setCategoryList2] = useState<MainCategory[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<StatusType[]>(
        []
    );

    const zoomLevel = userSettingsStore.getUserSetting(UserSettings.ZOOM_LEVEL);
    const { id } = useParams<{ id: string }>();
    const chartType = userSettingsStore.getUserSetting(UserSettings.CHART_TYPE);

    useEffect(() => {
        setTimestamp(moment());

        range1[1] = moment();
        range1[0] = moment().subtract(
            TimeConversion.DEFAULT_COMPARE_DAYS - 1,
            "days"
        );

        range2[0] = moment(range1[0]).subtract(
            TimeConversion.DEFAULT_COMPARE_DAYS,
            "days"
        );
        range2[1] = moment(range1[1]).subtract(
            TimeConversion.DEFAULT_COMPARE_DAYS,
            "days"
        );

        setCategoryList1(createCategoryList(isOutOfBedAvailable));

        setCategoryList2(createCategoryList(isOutOfBedAvailable));
    }, []);

    const createCategoryList = (outOfBedVisible: boolean = false) => {
        const OutOfBedIcon = STATUS_ID_TO_ICON[STATUS_ID.OutOfBed];
        const OutOfRoomIcon = STATUS_ID_TO_ICON[STATUS_ID.OutOfRoom];
        const InBathroomIcon = STATUS_ID_TO_ICON[STATUS_ID.InBathroom];
        const InRoomIcon = STATUS_ID_TO_ICON[STATUS_ID.InRoom];
        const InBedIcon = STATUS_ID_TO_ICON[STATUS_ID.InBed];

        const list = [
            {
                label: statusesTranslation("OutOfRoom"),
                color: "#868483",
                items: [],
                subcategories: [],
                expanded: expandedCategories.includes(StatusType.OutOfRoom),
                expandedLocked: true,
                typeId: StatusType.OutOfRoom,
                icon: <OutOfRoomIcon style={{ fill: "#868483" }} />,
            },
            {
                label: statusesTranslation("InBathroom"),
                color: "#2476B9",
                items: [],
                subcategories: [],
                expanded: expandedCategories.includes(StatusType.InBathroom),
                expandedLocked: true,
                typeId: StatusType.InBathroom,
                icon: <InBathroomIcon style={{ fill: "#2476B9" }} />,
            },
            {
                label: statusesTranslation("InRoom"),
                color: "#52D9E3",
                items: [],
                subcategories: [],
                expanded: expandedCategories.includes(StatusType.InRoom),
                expandedLocked: true,
                typeId: StatusType.InRoom,
                icon: <InRoomIcon style={{ fill: "#52D9E3" }} />,
            },
            {
                label: statusesTranslation("InBed"),
                color: "#064F58",
                items: [],
                subcategories: [],
                expanded: expandedCategories.includes(StatusType.InBed),
                expandedLocked: true,
                typeId: StatusType.InBed,
                icon: <InBedIcon style={{ fill: "#064F58" }} />,
            },
        ];

        if (outOfBedVisible) {
            list.push({
                label: statusesTranslation("OutOfBed"),
                color: "#7EBBBE",
                items: [],
                subcategories: [],
                expanded: expandedCategories.includes(StatusType.OutOfBed),
                expandedLocked: true,
                typeId: StatusType.OutOfBed,
                icon: <OutOfBedIcon style={{ fill: "#7EBBBE" }} />,
            });
        }

        return list;
    };

    const getDashboardDataDay = (date: Moment) => {
        let startTimestamp = moment(date)
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ss");
        let endTimestamp = moment(date)
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm:ss");

        if (date?.isSame(moment(), "day")) {
            startTimestamp = moment(date)
                .add(1, "hour")
                .subtract(1, "day")
                .format("YYYY-MM-DDTHH:00:00");
            endTimestamp = moment(date)
                .add(1, "hour")
                .format("YYYY-MM-DDTHH:00:00");
        }

        cleanItemList();

        requestDashboardData(startTimestamp, endTimestamp).then((list) =>
            setCategoryList1(list)
        );
    };

    const getDashboardDataHalfDay = (date: Moment) => {
        const {
            residentProfileIntervalDayStartTime,
            residentProfileIntervalNightStartTime,
        } = clientDashboardStore.clientDetails;

        if (
            !residentProfileIntervalDayStartTime ||
            !residentProfileIntervalNightStartTime
        )
            return;

        let startTimestamp =
            moment(date).subtract(1, "day").format("YYYY-MM-DDT") +
            residentProfileIntervalNightStartTime;
        let endTimestamp =
            moment(date).format("YYYY-MM-DDT") +
            residentProfileIntervalDayStartTime;

        cleanItemList();

        requestDashboardData(startTimestamp, endTimestamp).then((list) =>
            setCategoryList1(list)
        );
    };

    const getDashboardDataPeriod = () => {
        let startTimestamp1 = moment(range1[0])
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ss");
        let endTimestamp1 = moment(range1[1])
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm:ss");

        let startTimestamp2 = moment(range2[0])
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ss");
        let endTimestamp2 = moment(range2[1])
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm:ss");

        cleanItemList();

        requestDashboardData(startTimestamp1, endTimestamp1).then((list) =>
            setCategoryList1(list)
        );

        requestDashboardData(startTimestamp2, endTimestamp2).then((list) =>
            setCategoryList2(list)
        );
    };

    const requestDashboardData = (
        startTimestamp: string,
        endTimestamp: string
    ) => {
        return web
            .readDashboardData(
                Number(id),
                moment(startTimestamp).utc().format("YYYY-MM-DDTHH:mm:ss"),
                moment(endTimestamp).utc().format("YYYY-MM-DDTHH:mm:ss")
            )
            .then((response) => {
                const residentStatuses = response.data!.residentStatuses.filter(
                    (item) => {
                        return item.type !== 2;
                    }
                );

                const statuses = response.data!.statuses;

                const outOfBedIndex = statuses.findIndex((status) => {
                    return status.id === StatusType.OutOfBed;
                });

                let outOfBedInList = false;
                if (outOfBedIndex !== -1) {
                    setIsOutOfBedAvailable(true);
                    outOfBedInList = true;
                } else {
                    setIsOutOfBedAvailable(false);
                }

                const start = moment(startTimestamp).valueOf();
                const end = moment(endTimestamp).valueOf();

                const categoryList = mapItemList(
                    residentStatuses,
                    statuses,
                    start,
                    end,
                    outOfBedInList
                );

                return categoryList;
            });
    };

    const mapItemList = (
        residentStatuses: any[],
        statuses: any[],
        start: number,
        end: number,
        outOfBedInList: boolean
    ) => {
        const categoryList = mapSubCategories(statuses, outOfBedInList);

        residentStatuses.map((residentStatus, index) => {
            const itemData = mapDashboardItem(
                residentStatus,
                residentStatuses[index - 1],
                start,
                end
            );
            if (itemData) {
                if (residentStatus.parentTypeId) {
                    const parentIndex = categoryList.findIndex(
                        (value) => value.typeId === residentStatus.parentTypeId
                    );

                    categoryList[parentIndex].subcategories.map(
                        (subCat: any) => {
                            if (subCat.typeId === residentStatus.typeId) {
                                subCat.items.push(itemData);

                                categoryList[parentIndex].items.push({
                                    ...itemData,
                                });
                            }
                        }
                    );
                } else {
                    const parentIndex = categoryList.findIndex(
                        (value) => value.typeId === residentStatus.typeId
                    );
                    if (parentIndex !== -1)
                        categoryList[parentIndex].items.push(itemData);
                }
            }
        });

        return categoryList;
    };

    const mapDashboardItem = (
        currentResidentStatus: any,
        nextResidentStatus: any,
        startMS: number,
        endMS: number
    ) => {
        let residentStatusStartMS = moment(
            currentResidentStatus.timestamp
        ).valueOf();

        let residentStatusEndMS = moment().valueOf();

        if (residentStatusStartMS < startMS) residentStatusStartMS = startMS;
        if (residentStatusEndMS > endMS) residentStatusEndMS = endMS;

        if (nextResidentStatus) {
            residentStatusEndMS = moment(
                nextResidentStatus.timestamp
            ).valueOf();
        }

        const startMinutes = TimeConversion.millisecondsToMinutes(
            residentStatusStartMS - startMS
        );
        const endMinutes = TimeConversion.millisecondsToMinutes(
            residentStatusEndMS - startMS
        );

        if (residentStatusEndMS <= startMS || residentStatusStartMS >= endMS)
            return null;

        return {
            category: "",
            start: startMinutes,
            end: endMinutes,
            label: getMinutesToString(Math.round(endMinutes - startMinutes)),
        };
    };

    const mapSubCategories = (statuses: any[], outOfBedInList: boolean) => {
        let list: any[] = [...createCategoryList(outOfBedInList)];
        let colorCount = 0;

        list = list.map((item) => {
            item.items = [];
            item.subcategories = [];

            return item;
        });

        const createCategoryObject = (status: any, color: string): any => {
            return {
                label:
                    status.alternateText !== null
                        ? status.alternateText
                        : statusesTranslation(status.name),
                color: color,
                items: [],
                typeId: status.id,
                subcategories: [],
                icon: createImageFromURL(status.statusIcon),
                expandedLocked: true,
            };
        };

        const createSubCategoryObject = (status: any, color: string): any => {
            let icon = null;
            if (status.statusIcon) {
                icon = createImageFromURL(status.statusIcon);
            } else {
                icon = icons[status.id];
            }

            return {
                label:
                    status.alternateText !== null
                        ? status.alternateText
                        : statusesTranslation(status.name),
                color: color,
                items: [],
                typeId: status.id,
                icon: icon,
            };
        };

        statuses.map((status) => {
            if (ignoredStatuses.includes(status.id)) return;

            if (status.parentStatus === null) {
                const parentIndex = list.findIndex(
                    (value) => value.typeId === status.id
                );

                if (parentIndex === -1) {
                    const color = statusColorList[colorCount % 4];
                    colorCount++;
                    list.push(createCategoryObject(status, color));
                }
            } else {
                const parentIndex = list.findIndex(
                    (value) => value.typeId === status.parentStatus.id
                );

                if (parentIndex === -1) {
                    const color = statusColorList[colorCount % 4];
                    colorCount++;

                    const parent = createCategoryObject(
                        status.parentStatus,
                        color
                    );
                    parent.subcategories.push(
                        createSubCategoryObject(status, color)
                    );
                    parent.expandedLocked = false;
                    list.push(parent);
                } else {
                    list[parentIndex].subcategories.push(
                        createSubCategoryObject(status, list[parentIndex].color)
                    );
                    list[parentIndex].expandedLocked = false;
                }
            }
        });

        return list;
    };

    const cleanItemList = () => {
        const list1 = [...categoryList1];
        const list2 = [...categoryList2];

        list1.map((mainCat) => {
            mainCat.subcategories = [];
            if (mainCat.subcategories.length != 0) {
                mainCat.subcategories.map((subCat) => {
                    subCat.items = [];
                });
            }
            mainCat.items = [];
        });

        list2.map((mainCat) => {
            mainCat.subcategories = [];
            if (mainCat.subcategories.length != 0) {
                mainCat.subcategories.map((subCat) => {
                    subCat.items = [];
                });
            }
            mainCat.items = [];
        });

        setCategoryList1(list1);
        setCategoryList2(list2);
    };

    const createImageFromURL = (url: string) => {
        var img = <img width={24} height={24} src={url} />;

        return img;
    };

    useEffect(() => {
        if (!timestamp) return;
        switch (chartType) {
            case "daily":
                getDashboardDataDay(timestamp);
                break;
            case "night":
                getDashboardDataHalfDay(timestamp);
                break;
            case "compare":
                getDashboardDataPeriod();
                break;
        }
    }, [
        timestamp,
        range1,
        range2,
        chartType,
        clientDashboardStore.clientDetails,
    ]);

    const render = () => {
        return (
            <div className={styles["daily-view-container"]}>
                <div className={styles["chart-info"]}>
                    <TypeSelector />

                    <ViewSwitch activeKey={chartType}>
                        <ViewItem itemKey={["daily", "night"]}>
                            <div className={styles["chart-info"]}>
                                {LeftArrow()}
                                {DateComponent()}
                                {ArrowRight()}
                                {Today()}
                            </div>
                        </ViewItem>
                        <ViewItem itemKey={["compare"]}>
                            <div className={styles["chart-info"]}>
                                <div className={styles["multi-date-left"]}>
                                    {MultiDateComponent(
                                        valueChangeMultiDate1,
                                        range1,
                                        "range1"
                                    )}
                                </div>

                                <div className={styles["multi-date-text"]}>
                                    {t("With")}
                                </div>
                                <CompareDropdown
                                    onChange={valueChangeMultiDate2}
                                />
                            </div>
                        </ViewItem>
                    </ViewSwitch>

                    {ZoomLevel()}
                </div>
                <ViewSwitch activeKey={chartType}>
                    <ViewItem itemKey={["daily"]}>
                        <div className={styles["daily-chart"]}>
                            <div className={styles["container"]}>
                                <CategoryMenu
                                    categories={getCategories()}
                                    onCategoryClick={handleCatClick}
                                />
                                <div
                                    className={
                                        styles["canvas-scroll-container"]
                                    }
                                >
                                    <div
                                        className={
                                            styles["canvas-layout-container"]
                                        }
                                    >
                                        <CanvasChart
                                            canvasId="daily-chart"
                                            parts={getParts("daily")}
                                            barData={getDailyItems()}
                                            categories={getCategories()}
                                            yLabels={getYLabels()}
                                            drawBarConnectionLines={true}
                                            zoomLevel={zoomLevel}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ViewItem>

                    <ViewItem itemKey={["night"]}>
                        <div className={styles["daily-chart"]}>
                            <div className={styles["container"]}>
                                <CategoryMenu
                                    categories={getCategories()}
                                    onCategoryClick={handleCatClick}
                                />
                                <div
                                    className={
                                        styles["canvas-scroll-container"]
                                    }
                                >
                                    <div
                                        className={
                                            styles["canvas-layout-container"]
                                        }
                                    >
                                        <CanvasChart
                                            canvasId="night-chart"
                                            parts={getParts("night")}
                                            barData={getDailyItems()}
                                            categories={getCategories()}
                                            yLabels={getNightTimeYLabels()}
                                            drawBarConnectionLines={true}
                                            zoomLevel={zoomLevel}
                                            splits={getNightTimeSplits()}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ViewItem>

                    <ViewItem itemKey={["compare"]}>
                        <div className={styles["daily-chart"]}>
                            <div className={styles["compare-container"]}>
                                <div className={styles["category-container"]}>
                                    <div className={styles["category-group"]}>
                                        <div
                                            className={styles["date-container"]}
                                        >
                                            <div>
                                                {formatMoment(
                                                    range1[0],
                                                    "DD MMM"
                                                )}
                                            </div>
                                            <div>-</div>
                                            <div>
                                                {formatMoment(
                                                    range1[1],
                                                    "DD MMM"
                                                )}
                                            </div>
                                        </div>

                                        <CategoryMenu
                                            categories={getCategories()}
                                            onCategoryClick={handleCatClick}
                                        />
                                    </div>

                                    <div className={styles["category-group"]}>
                                        <div
                                            className={styles["date-container"]}
                                        >
                                            <div>
                                                {" "}
                                                {formatMoment(
                                                    range2[0],
                                                    "DD MMM"
                                                )}
                                            </div>
                                            <div>-</div>
                                            <div>
                                                {formatMoment(
                                                    range2[1],
                                                    "DD MMM"
                                                )}
                                            </div>
                                        </div>

                                        <CategoryMenu
                                            categories={getCategories()}
                                            onCategoryClick={handleCatClick}
                                        />
                                    </div>
                                </div>
                                <div
                                    className={
                                        styles["canvas-scroll-container"]
                                    }
                                >
                                    <div
                                        className={
                                            styles["canvas-layout-container"]
                                        }
                                    >
                                        <CanvasChart
                                            canvasId="range1-chart"
                                            parts={getParts("range1")}
                                            barData={getDailyItems()}
                                            categories={getCategories()}
                                            drawBarConnectionLines={true}
                                            zoomLevel={zoomLevel}
                                            splits={getRange1TimeSplits()}
                                        />
                                        <CanvasChart
                                            canvasId="range2-chart"
                                            parts={getParts("range2")}
                                            barData={getDailyItems2()}
                                            categories={getCategories()}
                                            drawBarConnectionLines={true}
                                            zoomLevel={zoomLevel}
                                            yLabels={getCompareTimeYLabels()}
                                            splits={getRange2TimeSplits()}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ViewItem>
                </ViewSwitch>
            </div>
        );
    };

    const getDailyItems = () => {
        const list: any[] = [];

        let index = 0;

        categoryList1.map((mainCat) => {
            mainCat.items.map((item) => {
                item.category = index;
                list.push(item);
            });

            index++;

            if (mainCat.subcategories && mainCat.expanded) {
                mainCat.subcategories.map((subCat) => {
                    subCat.items.map((item) => {
                        item.category = index;
                        list.push(item);
                    });
                    index++;
                });
            }
        });

        return list;
    };

    const getDailyItems2 = () => {
        const list: any[] = [];

        let index = 0;

        categoryList2.map((mainCat) => {
            mainCat.items.map((item) => {
                item.category = index;
                list.push(item);
            });

            index++;

            if (mainCat.subcategories && mainCat.expanded) {
                mainCat.subcategories.map((subCat) => {
                    subCat.items.map((item) => {
                        item.category = index;
                        list.push(item);
                    });
                    index++;
                });
            }
        });

        return list;
    };

    const getParts = (type: string) => {
        const {
            residentProfileIntervalDayStartTime,
            residentProfileIntervalNightStartTime,
        } = clientDashboardStore.clientDetails;

        let startTimestamp = moment(
            moment(timestamp).subtract(1, "day").format("YYYY-MM-DDT") +
                residentProfileIntervalNightStartTime
        );
        let endTimestamp = moment(
            moment(timestamp).format("YYYY-MM-DDT") +
                residentProfileIntervalDayStartTime
        );

        if (type === "daily") {
            return 1440;
        } else if (type === "night") {
            return endTimestamp.diff(startTimestamp, "minutes");
        } else if (type === "range1") {
            if (range1[0] && range1[1])
                return (range1[1]!.diff(range1[0]!, "days") + 1) * 1440;
        } else if (type === "range2") {
            if (range2[0] && range2[1])
                return (range2[1]!.diff(range2[0]!, "days") + 1) * 1440;
        }

        return 0;
    };

    const valueChangeMultiDate1 = (values: Moment[]) => {
        setRange1(values);

        const rangeDaysNumber = moment(values[1]).diff(values[0], "days");

        const range2LowerLimit = moment(values[0]).subtract(
            TimeConversion.DAYS_IN_WEEK * compareWeekNumber,
            "days"
        );

        setRange2([
            range2LowerLimit,
            moment(range2LowerLimit).add(rangeDaysNumber, "days"),
        ]);
    };

    const valueChangeMultiDate2 = (value: number) => {
        const rangeDaysNumber = moment(range1[1]).diff(range1[0], "days");

        const range2LowerLimit = moment(range1[0]).subtract(
            TimeConversion.DAYS_IN_WEEK * value,
            "days"
        );

        setCompareWeekNumber(value);

        setRange2([
            range2LowerLimit,
            moment(range2LowerLimit).add(rangeDaysNumber, "days"),
        ]);
    };

    const getNightTimeSplits = () => {
        const {
            residentProfileIntervalDayStartTime,
            residentProfileIntervalNightStartTime,
        } = clientDashboardStore.clientDetails;

        let startTimestamp = moment(
            moment(timestamp).subtract(1, "day").format("YYYY-MM-DDT") +
                residentProfileIntervalNightStartTime
        );

        let endTimestamp = moment(
            moment(timestamp).format("YYYY-MM-DDT") +
                residentProfileIntervalDayStartTime
        );

        return [
            {
                label: formatMoment(
                    moment(timestamp).subtract(1, "day"),
                    "DD MMM"
                ),
                start: 0,
                end: moment(timestamp)
                    .startOf("day")
                    .diff(startTimestamp, "minutes"),
            },
            {
                label: formatMoment(timestamp, "DD MMM"),
                start: moment(timestamp)
                    .startOf("day")
                    .diff(startTimestamp, "minutes"),
                end: endTimestamp.diff(startTimestamp, "minutes"),
            },
        ];
    };

    const getRange1TimeSplits = () => {
        const splits = [];

        const date = moment(range1[0]);
        let index = 0;

        if (range1[0] && range1[1]) {
            const totalParts = (range1[1]!.diff(range1[0]!, "days") + 1) * 1440;
            const partsPerSplit = 1440;

            while (date.isSameOrBefore(range1[1])) {
                let end = (index + 1) * partsPerSplit;
                if (end > totalParts) end = totalParts;

                splits.push({
                    label: formatMoment(date, "DD MMM"),
                    start: index * partsPerSplit,
                    end: end,
                });
                date.add(1, "days");
                index++;
            }
        }

        return splits;
    };

    const getRange2TimeSplits = () => {
        const splits = [];

        const date = moment(range2[0]);
        let index = 0;

        if (range2[0] && range2[1]) {
            const totalParts = (range2[1]!.diff(range2[0]!, "days") + 1) * 1440;
            const partsPerSplit = 1440;

            while (date.isSameOrBefore(range2[1])) {
                let end = (index + 1) * partsPerSplit;
                if (end > totalParts) end = totalParts;

                splits.push({
                    label: formatMoment(date, "DD MMM"),
                    start: index * partsPerSplit,
                    end: end,
                });
                date.add(1, "days");
                index++;
            }
        }

        return splits;
    };

    const handleCatClick = (categoryId: number) => {
        const index = categoryList1.findIndex((value) => {
            return value.typeId === categoryId;
        });

        if (expandedCategories.includes(categoryId)) {
            const newExpandedList = expandedCategories.filter(
                (id) => id !== categoryId
            );

            if (categoryList1[index]) categoryList1[index].expanded = false;
            if (categoryList2[index]) categoryList2[index].expanded = false;

            setExpandedCategories(newExpandedList);
        } else {
            const newExpandedList = [...expandedCategories];
            newExpandedList.push(categoryId);

            if (categoryList1[index]) categoryList1[index].expanded = true;
            if (categoryList2[index]) categoryList2[index].expanded = true;

            setExpandedCategories(newExpandedList);
        }
    };

    const lastValidDate = () => {
        return moment(clientDashboardStore.lastValidTimestamp);
    };

    const disableDays = (date: Moment) => {
        const isDateAfterLastValidDate = date.isBefore(lastValidDate(), "days");
        return date.isAfter() || isDateAfterLastValidDate;
    };

    const DateComponent = () => {
        return (
            <PickerWithButtonField
                value={timestamp}
                shouldDisableDate={disableDays}
                label={getLabel()}
                onChange={(value) => {
                    if (!value) return;
                    let offsetDate = value?.startOf("day");

                    if (offsetDate.isSameOrAfter(moment(), "day")) {
                        offsetDate = moment();
                    }

                    setTimestamp(offsetDate);
                }}
            />
        );
    };

    const MultiDateComponent = (
        onChange: any,
        range: (Moment | null)[],
        type: string
    ) => {
        let label = "";

        if (range[0] && range[1]) {
            label =
                formatMoment(range[0], "DD MMM") +
                " - " +
                formatMoment(range[1], "DD MMM");
        }

        let dayLimit = TimeConversion.DEFAULT_COMPARE_DAYS;

        if (type === "range2" && range1[0] && range1[1]) {
            dayLimit = moment(range1[1]).diff(range1[0], "days");
        }

        let forcedDays = undefined;
        if (type === "range2" && range1[0] && range1[1]) {
            forcedDays = range1[1].diff(range1[0], "days") + 1;
        }

        return (
            <MultiPickerWithButtonField
                value={range}
                shouldDisableDate={disableDays}
                label={label}
                onChange={onChange}
                dayLimit={dayLimit}
                forcedRange={forcedDays}
            />
        );
    };

    const getLabel = () => {
        let day = "";

        if (chartType === "night") {
            day =
                formatMoment(moment(timestamp!).subtract(1, "days"), "DD") +
                "-" +
                formatMoment(timestamp!, "DD");
        } else {
            if (timestamp?.isSame(moment(), "day")) {
                day =
                    formatMoment(moment().subtract(1, "days"), "DD") +
                    "-" +
                    formatMoment(moment(), "DD");
            } else {
                if (timestamp) day = formatMoment(timestamp!, "DD");
            }
        }

        const label =
            timestamp == null ? "" : formatMoment(timestamp, "MMM YYYY");
        return day + " " + label;
    };

    const ArrowRight = () => {
        if (!timestamp) return;

        let enabled = false;

        if (moment().diff(timestamp, "days") > 0) {
            enabled = true;
        }

        return RightArrowButton({
            isEnabled: enabled,
            onArrowClick: onClickRightArrow,
        });
    };

    const LeftArrow = () => {
        let enabled = false;
        let isDateSameAsLastValidDate = false;

        if (timestamp)
            isDateSameAsLastValidDate = timestamp.isSame(
                lastValidDate(),
                "days"
            );

        enabled = !isDateSameAsLastValidDate;

        return LeftArrowButton({
            isEnabled: enabled,
            onArrowClick: onClickLeftArrow,
        });
    };

    const Today = () => {
        if (!timestamp) return;

        const enabled = !timestamp?.isSame(moment(), "day");

        return TodayButton({
            isEnabled: enabled,
            onButtonClick: onClickToday,
            t: t,
        });
    };

    const ZoomLevel = () => {
        return <ZoomLevelToggle />;
    };

    const getCategories = () => {
        const categories: any[] = [];

        categoryList1.forEach((mainCat: MainCategory) => {
            categories.push({
                color: mainCat.color,
                icon: mainCat.icon,
                labels: [mainCat.label],
                id: mainCat.typeId,
                expanded: mainCat.expanded,
                expandedLocked: mainCat.expandedLocked,
            });

            if (mainCat.expanded) {
                mainCat.subcategories.forEach((subCat: SubCategory) => {
                    categories.push({
                        color: subCat.color,
                        icon: subCat.icon,
                        labels: [subCat.label],
                        id: subCat.typeId,
                        expanded: null,
                    });
                });
            }
        });

        return categories;
    };

    const getMinutesToString = (minutes: number) => {
        let label = "";

        const hours = Math.floor(TimeConversion.minutesToHour(minutes));
        if (hours > 0) {
            label = `${hours}h`;
        }
        if (minutes - TimeConversion.hourToMinutes(hours) > 0) {
            label = label + `${minutes - TimeConversion.hourToMinutes(hours)}m`;
        }
        return label;
    };

    const getHoursToString = (hour: number, lowerHour: number) => {
        let shownHour = hour + lowerHour;
        if (shownHour > 24) shownHour = shownHour - 24;

        if (shownHour === 24) return "00:00";
        if (shownHour < 10) return `0${shownHour}:00`;
        return `${shownHour}:00`;
    };

    const getYLabels = () => {
        const labels = new Map<number, string>();
        let lowerHour = 0;

        if (timestamp) {
            if (timestamp?.isSame(moment(), "day"))
                lowerHour = moment(timestamp).add(1, "hour").hour();
            else {
                lowerHour = moment(timestamp).hour();
            }
        }

        for (let hour = 0; hour <= 24; hour += 2) {
            labels.set(
                TimeConversion.hourToMinutes(hour),
                getHoursToString(hour, lowerHour)
            );
        }

        return labels;
    };

    const getNightTimeYLabels = () => {
        const {
            residentProfileIntervalDayStartTime,
            residentProfileIntervalNightStartTime,
        } = clientDashboardStore.clientDetails;

        let startTimestamp = moment(
            moment(timestamp).subtract(1, "day").format("YYYY-MM-DDT") +
                residentProfileIntervalNightStartTime
        );
        let endTimestamp = moment(
            moment(timestamp).format("YYYY-MM-DDT") +
                residentProfileIntervalDayStartTime
        );

        const labels = new Map<number, string>();

        const increment = 2;

        const iTimestamp = moment(startTimestamp);

        while (iTimestamp.isSameOrBefore(endTimestamp)) {
            const diffMinutes = Math.abs(
                startTimestamp.diff(iTimestamp, "minutes")
            );

            labels.set(diffMinutes, moment(iTimestamp).format("HH:mm"));
            iTimestamp.add(increment, "hours");
        }

        return labels;
    };

    const getCompareTimeYLabels = () => {
        const labels = new Map<number, string>();

        if (range1[0] && range2[0]) {
            const daysRange2 = range2[1]!.diff(range2[0]!, "days") + 1;

            for (let d = 0; d < daysRange2; d++) {
                for (let h = 0; h < 2; h++) {
                    labels.set(
                        h * 12 * 60 + d * 1440,
                        getHoursToString(h * 12, 0)
                    );
                }
            }

            labels.set(daysRange2 * 1440, getHoursToString(0, 0));
        }

        return labels;
    };

    const onClickLeftArrow = () => {
        if (!timestamp) {
            return;
        }

        const date = timestamp.clone().subtract(1, "days").startOf("day");

        setTimestamp(date);
    };

    const onClickRightArrow = () => {
        if (!timestamp) {
            return;
        }

        let date = timestamp.clone().add(1, "day").startOf("day");

        if (date.isSameOrAfter(moment(), "day")) {
            date = moment();
        }

        setTimestamp(date);
    };

    const onClickToday = () => {
        if (!timestamp) {
            return;
        }

        const date = moment();
        setTimestamp(date);
    };

    const formatMoment = (timestamp: MomentInput, format: string) => {
        return moment(timestamp)
            .locale(navigator.language.substring(0, 2)) //TODO: make locale global on page load
            .format(format);
    };

    return render();
}

export default inject(
    "clientDashboardStore",
    "userSettingsStore"
)(observer(DailyView));
